"use client";

import {JSX, useEffect, useRef, useState} from "react";

import SmartImage from "@/shared/ui/smart-image";

import {Amenity} from "@/entities/rental";

import {useTranslation} from "@/shared/i18n";
import {ButtonBase} from "@/shared/ui/buttons/button-base";
import {Typography} from "@/shared/ui/data-display/typography";
import {assetPath} from "@/shared/utils/asset-path";

import "./styles/amenities-all.scss";

export interface AmenityItem {
    id: number;
    icon: string;
    alt: string;
    text: string;
}

export interface AmenitiesAllProps {
    title: string;
    amenities: Array<AmenityItem>;
    enableShowAll?: boolean;
    initialVisibleCount?: number;
    className?: string;
}

export function AmenitiesAll({title, amenities, enableShowAll = false, initialVisibleCount = 6, className = ""}: AmenitiesAllProps): JSX.Element {
    const {t} = useTranslation("rental");
    const [isExpanded, setIsExpanded] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);
    const COLLAPSED_HEIGHT = 68;
    const [maxHeight, setMaxHeight] = useState<number | undefined>(enableShowAll ? COLLAPSED_HEIGHT : undefined);

    useEffect(() => {
        if (contentRef.current && enableShowAll) {
            if (isExpanded) {
                setMaxHeight(contentRef.current.scrollHeight);
            } else {
                setMaxHeight(COLLAPSED_HEIGHT);
            }
        }
    }, [isExpanded, enableShowAll]);

    const handleToggle = (): void => {
        setIsExpanded(!isExpanded);
    };

    return (
        <section className={`amenities-all ${className}`}>
            <Typography as="h2" size={{desktop: 24, mobile: 20}} weight={600} className="amenities-all__title">
                {title}
            </Typography>

            <div
                ref={contentRef}
                className={`amenities-all__container ${enableShowAll && !isExpanded ? "amenities-all__container--collapsed" : ""}`}
                style={enableShowAll ? {maxHeight: maxHeight !== undefined ? `${maxHeight}px` : undefined} : undefined}
            >
                <div className="amenities-all__grid">
                    {amenities.map((amenity) => (
                        <Amenity key={amenity.id} icon={amenity.icon} alt={amenity.alt} text={amenity.text} className="amenities-all__item" />
                    ))}
                </div>
            </div>

            {enableShowAll && amenities.length > initialVisibleCount && (
                <ButtonBase variant="secondary-outline" size="sm" onClick={handleToggle} className="amenities-all__button">
                    {isExpanded ? t("showLessAmenities") : t("showAllAmenities")}
                    <SmartImage
                        src={assetPath("/images/icons/arrow-black-down.svg")}
                        alt="Arrow"
                        width={11}
                        height={7}
                        className={`amenities-all__arrow ${isExpanded ? "amenities-all__arrow--up" : ""}`}
                    />
                </ButtonBase>
            )}
        </section>
    );
}
